import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

export type SyllabusDashboard = {
  academicSession: string
  adoptions: number
  publishedAdoptions: number
  subjectsConfigured: number
  subjectsPublished: number
  pendingAdoptions: number
  pendingSyllabi: number
  frameworks: string[]
}

export type CurriculumAdoption = {
  _id: string
  boardId: string
  frameworkId: string
  versionId: string
  versionCode: string
  title: string
  status: string
  gradeLabels?: string[]
}

export type SyllabusTopicRow = {
  _id: string
  title: string
  stableKey: string
  nodeType: string
  inclusionStatus: string
  sequence: number
  estimatedPeriods?: number | null
  priority?: string
  targetCompletion?: string
  coverageLevel?: string
  remarks?: string
  curriculumNodeId?: string
}

export type SyllabusMatrix = {
  version: { _id: string; status: string; revisionNumber: number }
  subject: { _id: string; subjectName: string; capabbleClassKey: string }
  adoption: CurriculumAdoption
  topics: SyllabusTopicRow[]
  textbooks: unknown[]
}

const base = '/acdmc/syllabus'

export const fetchSyllabusDashboard = () =>
  api.get<{ success: boolean; data: SyllabusDashboard }>(`${base}/dashboard`).then((r) => r.data.data)

export const fetchAdoptions = () =>
  api.get<{ success: boolean; data: CurriculumAdoption[] }>(`${base}/adoptions`).then((r) => r.data.data)

export const fetchPlatformVersions = (frameworkId?: string) =>
  api
    .get<{ success: boolean; data: Array<{ _id: string; versionCode: string }> }>(`${base}/platform/versions`, {
      params: frameworkId ? { frameworkId } : undefined,
    })
    .then((r) => r.data.data)

export const fetchMatrix = (versionId: string) =>
  api.get<{ success: boolean; data: SyllabusMatrix }>(`${base}/versions/${versionId}/matrix`).then((r) => r.data.data)

export const createAdoption = (body: {
  boardId: string
  frameworkId: string
  versionId: string
  title?: string
  boardCode?: string
  frameworkCode?: string
  gradeLabels?: string[]
}) => api.post(`${base}/adoptions`, body).then((r) => r.data)

export const addAdoptionSubjects = (
  adoptionId: string,
  subjects: Array<{ capabbleClassKey: string; subjectName: string; curriculumSubjectId?: string; frameworkGradeLabel?: string }>,
) => api.post(`${base}/adoptions/${adoptionId}/subjects`, { subjects }).then((r) => r.data)

export const bulkUpdateTopics = (versionId: string, updates: Partial<SyllabusTopicRow> & { id: string }[]) =>
  api.patch(`${base}/versions/${versionId}/topics`, { updates }).then((r) => r.data)

export const transitionSyllabus = (versionId: string, status: string) =>
  api.post(`${base}/versions/${versionId}/transition`, { status }).then((r) => r.data)

export function useSyllabusDashboard() {
  return useQuery({ queryKey: ['acdmc', 'syllabus', 'dashboard'], queryFn: fetchSyllabusDashboard })
}

export function useAdoptions() {
  return useQuery({ queryKey: ['acdmc', 'syllabus', 'adoptions'], queryFn: fetchAdoptions })
}

export function useSyllabusMatrix(versionId: string | null) {
  return useQuery({
    queryKey: ['acdmc', 'syllabus', 'matrix', versionId],
    queryFn: () => fetchMatrix(versionId!),
    enabled: Boolean(versionId),
  })
}

export function useCreateAdoption() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createAdoption,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc', 'syllabus'] })
    },
  })
}

export function useBulkUpdateTopics(versionId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (updates: Partial<SyllabusTopicRow> & { id: string }[]) => bulkUpdateTopics(versionId, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc', 'syllabus', 'matrix', versionId] })
    },
  })
}
